import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { useAppDispatch } from '../../app/hooks';
import { routeReportTypeMap } from '../../app/routes';
import DashboardLayout from '../../common/layouts/Dashboard';
import {
  addBreadcrumb,
  BreadcrumbType,
  editBreadcrumbs,
  ReplaceType,
} from '../../common/utils/editBreadcrums';
import { getPageTitle } from '../../common/utils/pageUtils';
import { PageProps } from '../../types/PageProps';
import { clearCredentials } from '../auth/authSlice';
import { setMonitorMode } from '../monitor/monitorSlide';
import { clearAssessmentCachedData } from '../patient/patientUtils';
import { StyledButton } from './ReadyOrResumeInterview';
import { StyledBox } from './StartInterviewPage';
import { setClinicalFollowUps, setInterviewMode } from './interviewSlice';

export default function ContinueNextInterviewPage({ breadcrumbs }: PageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { patientId, interviewResultId: inputInterviewResultId, reportType } = useParams();
  const interviewResultId = parseInt(inputInterviewResultId!);
  const replacePath: ReplaceType[] = [{ param: ':patientId', value: patientId! }]; // TODO: should create common logic?
  const newBreadcrumb: BreadcrumbType = {
    text: 'Patient Dashboard',
    link: `/dashboard/patient/${patientId}`,
  };
  const { pathname } = useLocation();
  const isInterview = !!pathname.includes('continue-next-interview');

  useEffect(() => {
    if (patientId && interviewResultId) {
      dispatch(setClinicalFollowUps({ patientId, interviewResultId }));
    }
  }, [dispatch, interviewResultId, patientId]);

  const renderStaticCompletePageBody = () => {
    return (
      <>
        <Typography variant="subtitle1">{t('interview.continueNextInterview.title')}</Typography>
      </>
    );
  };

  return (
    <DashboardLayout
      breadcrumbs={editBreadcrumbs(addBreadcrumb(breadcrumbs!, newBreadcrumb), ...replacePath)}
      showBreadcrumb={false}
    >
      <Helmet>
        <title>{getPageTitle(t('interview.title'))}</title>
      </Helmet>
      <Container maxWidth="xl" disableGutters={true}>
        <StyledBox>
          <Box sx={{ py: 3 }}>
            <Typography variant="h1">
              {t('interview.continueNextInterview.completePage')}
            </Typography>
          </Box>
          <>
            <Box sx={{ mb: 1 }}>{renderStaticCompletePageBody()}</Box>
            <Box
              sx={{
                py: 2,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <StyledButton
                type="button"
                variant="contained"
                onClick={async () => {
                  if (isInterview) {
                    dispatch(
                      setInterviewMode({
                        interviewMode: 'P',
                        reportType: reportType!,
                      })
                    );
                    dispatch(clearCredentials());
                    clearAssessmentCachedData();
                    const interviewConductUrl = `/current-interview/${
                      routeReportTypeMap[reportType!]
                    }/${inputInterviewResultId}`;
                    navigate(interviewConductUrl);
                  } else {
                    dispatch(setMonitorMode({ monitorMode: 'P' }));
                    dispatch(clearCredentials());
                    const monitorConductUrl = `/current-monitor/screenerPage/${inputInterviewResultId}`;
                    navigate(monitorConductUrl);
                  }
                }}
              >
                {t('interview.continueNextInterview.continue')}
              </StyledButton>
            </Box>
          </>
        </StyledBox>
      </Container>
    </DashboardLayout>
  );
}
